import { useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'
import { useStore } from '@Store/'
import { useInView } from 'react-intersection-observer'

const useAttract = options => {
  const settings = {
    animate: true,
    fixed: false,
    threshold: 0,
    contain: false,
    ...options,
  }
  const { animate, fixed, threshold, contain } = settings
  const [store] = useStore()
  const [inViewRef, inView] = useInView()
  const $attractTarget = useRef()
  const $attractField = useRef()
  const targetBounds = useRef()
  const attractFieldBounds = useRef()
  const isInView = useRef()
  const prevMousePosition = useRef()
  const mousePosition = useRef()
  const smoothScroll = useRef()
  const play = useRef()
  const attract = useRef(true)
  const clear = useRef(true)

  const setAttractRefs = node => {
    $attractTarget.current = node
    $attractField.current = node
    inViewRef(node)
  }

  // Use this when the area you would like attraction to occur is different to the element being attracted
  const setAttractFieldRefs = node => {
    $attractField.current = node
    inViewRef(node)
  }

  const renderLoop = useCallback(() => {
    if (
      !isInView.current ||
      !attract.current ||
      !play.current ||
      !attractFieldBounds.current ||
      prevMousePosition.current === mousePosition.current
    )
      return

    const scrollTop = smoothScroll.current.scrollTop()
    const mouseX = mousePosition.current[0]

    // mouseY is an absolute value from the top of the page; when !fixed
    const mouseY = fixed
      ? mousePosition.current[1]
      : mousePosition.current[1] + scrollTop

    const inRange =
      mouseX > attractFieldBounds.current.left &&
      mouseX < attractFieldBounds.current.right &&
      mouseY > attractFieldBounds.current.top &&
      mouseY < attractFieldBounds.current.bottom

    const targetX = mouseX - targetBounds.current.xCenter
    const targetY = mouseY - targetBounds.current.yCenter
    let clampedTargetX = targetX
    let clampedTargetY = targetY

    if (contain) {
      clampedTargetX = gsap.utils.clamp(
        attractFieldBounds.current.left - targetBounds.current.left,
        attractFieldBounds.current.right -
          targetBounds.current.left -
          targetBounds.current.width,
        targetX
      )
      clampedTargetY = gsap.utils.clamp(
        attractFieldBounds.current.top -
          targetBounds.current.yCenter +
          targetBounds.current.height / 2,
        attractFieldBounds.current.bottom -
          targetBounds.current.yCenter -
          targetBounds.current.height / 2,
        targetY
      )
    }

    if (inRange) {
      gsap.to($attractTarget.current, {
        x: clampedTargetX,
        y: clampedTargetY,
        duration: 0.3,
      })
      clear.current = false
    } else {
      if (!clear.current) {
        gsap.killTweensOf([$attractTarget.current])

        gsap.to([$attractTarget.current], {
          x: 0,
          y: 0,
          duration: 0.2,
        })
        clear.current = true
      }
    }

    prevMousePosition.current = mousePosition.current
  }, [fixed, contain])

  const setBounds = useCallback(() => {
    if (!smoothScroll.current || !$attractTarget.current) return

    // Clear transforms on the $attractTarget to ensure the correct bounds are calculated. Otherwise, if a button is hovered at the point of resize then the bounds will be incorrect
    gsap.set($attractTarget.current, {
      clearProps: 'transform',
    })

    const scrollTop = smoothScroll.current.scrollTop()

    // getBoundingClientRect returns the relative position from the top of the page taking into account scrollTop. So if the page is scrolled it will be a lower value
    // To simplify the upcoming calculations we set this as an absolute value from the top of the page
    targetBounds.current = $attractTarget.current.getBoundingClientRect()
    targetBounds.current.xCenter =
      targetBounds.current.left + targetBounds.current.width / 2
    targetBounds.current.yCenter =
      targetBounds.current.top + scrollTop + targetBounds.current.height / 2

    if (contain) {
      const bounds = $attractField.current.getBoundingClientRect()
      const { top, left, width, height } = bounds

      attractFieldBounds.current = {
        top: top + scrollTop,
        bottom: top + scrollTop + height,
        left: left,
        right: left + width,
        xCenter: left + width / 2,
        yCenter: top + scrollTop + height / 2,
      }
    } else {
      const { top, left, width, height } = targetBounds.current
      attractFieldBounds.current = {
        top: top + scrollTop - threshold,
        bottom: top + scrollTop + height + threshold,
        left: left - threshold,
        right: left + width + threshold,
        xCenter: left + width / 2,
        yCenter: top + scrollTop + height / 2,
      }
    }
  }, [threshold, contain])

  useEffect(() => {
    const safeToAnimate = window.matchMedia(
      '(prefers-reduced-motion: no-preference)'
    ).matches

    if (!safeToAnimate || !store.fontIsLoaded || !store.smoothScroll) return

    const $target = $attractTarget.current
    smoothScroll.current = store.smoothScroll

    setBounds()
    gsap.ticker.add(renderLoop)

    window.addEventListener('resize', setBounds)

    return () => {
      if (!safeToAnimate) return

      gsap.ticker.remove(renderLoop)
      window.removeEventListener('resize', setBounds)

      // On unmount return element to it's original position
      if ($target) {
        gsap.to($target, {
          x: 0,
          y: 0,
          ease: 'out',
          duration: 0.2,
        })
      }
    }
  }, [store.fontIsLoaded, store.smoothScroll, setBounds, renderLoop])

  // On page dimensions change
  useEffect(() => {
    setBounds()
  }, [store.resized, setBounds])

  // Update variable refs
  useEffect(() => {
    mousePosition.current = store.mousePosition
    play.current = animate
    isInView.current = inView
  }, [store.mousePosition, animate, inView])

  return {
    $attractTarget,
    $attractField,
    setAttractRefs,
    setAttractFieldRefs,
    inViewRef,
    setAttractionBounds: setBounds,
  }
}

useAttract.propTypes = {
  options: PropTypes.object,
}

export default useAttract
